import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check, Crown, Sparkles, Star, Loader2, CreditCard, ArrowLeft, Home } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";

interface Package {
  id: string;
  name_en: string;
  name_ar: string;
  credits: number;
  price_sar: number;
  description_en: string | null;
  description_ar: string | null;
  stripe_price_id: string | null;
}

interface UserCredits {
  total_credits: number;
  used_credits: number;
}

const Packages = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === "ar";
  
  const [packages, setPackages] = useState<Package[]>([]);
  const [userCredits, setUserCredits] = useState<UserCredits | null>(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState<string | null>(null);
  const [user, setUser] = useState<any>(null);

  useEffect(() => {
    fetchPackages();
    checkUser();
  }, []);

  const checkUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setUser(user);
    if (user) {
      fetchUserCredits(user.id);
    }
  };

  const fetchPackages = async () => {
    const { data, error } = await supabase
      .from("subscription_packages")
      .select("*")
      .eq("is_active", true)
      .order("credits", { ascending: true });

    if (data) {
      setPackages(data);
    }
    setLoading(false);
  };

  const fetchUserCredits = async (userId: string) => {
    const { data } = await supabase
      .from("user_credits")
      .select("*")
      .eq("user_id", userId)
      .maybeSingle();

    if (data) {
      setUserCredits(data);
    }
  };

  const handlePurchase = async (pkg: Package) => {
    if (!user) {
      toast.error(isRTL ? "يجب تسجيل الدخول أولاً" : "Please login first");
      navigate("/auth");
      return;
    }


    setPurchasing(pkg.id);

    try {
      const { data, error } = await supabase.functions.invoke("create-paymob-payment", {
        body: { packageId: pkg.id },
      });

      if (error) throw error;

      if (data?.url) {
        window.open(data.url, "_blank");
      }
    } catch (error: any) {
      console.error("Payment error:", error);
      toast.error(isRTL ? "حدث خطأ في الدفع" : "Payment error occurred");
    } finally {
      setPurchasing(null);
    }
  };

  const getPackageIcon = (credits: number) => {
    if (credits >= 10) return <Crown className="h-8 w-8 text-yellow-500" />;
    if (credits >= 5) return <Sparkles className="h-8 w-8 text-purple-500" />;
    return <Star className="h-8 w-8 text-blue-500" />;
  };

  const getPackageStyle = (credits: number) => {
    if (credits >= 10) return "border-yellow-500/50 bg-gradient-to-br from-yellow-500/10 to-orange-500/10";
    if (credits >= 5) return "border-purple-500/50 bg-gradient-to-br from-purple-500/10 to-pink-500/10";
    return "border-blue-500/50 bg-gradient-to-br from-blue-500/10 to-cyan-500/10";
  };

  const remainingCredits = userCredits ? userCredits.total_credits - userCredits.used_credits : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" dir={isRTL ? "rtl" : "ltr"}>
      <Navbar />
      
      <div className="container mx-auto px-4 py-8 pt-24">
        {/* Back to Home Button */}
        <div className="mb-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate("/")}
            className="gap-2 text-muted-foreground hover:text-foreground"
          >
            {isRTL ? <ArrowLeft className="h-4 w-4 rotate-180" /> : <ArrowLeft className="h-4 w-4" />}
            <Home className="h-4 w-4" />
            <span>{isRTL ? "الصفحة الرئيسية" : "Home"}</span>
          </Button>
        </div>
        
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">
            {isRTL ? "باقات الإضافة" : "Submission Packages"}
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            {isRTL 
              ? "اختر الباقة المناسبة لإضافة فعالياتك ومساحاتك وحساباتك"
              : "Choose the right package to add your events, spaces, and accounts"}
          </p>
        </div>

        {/* User Credits */}
        {user && (
          <div className="max-w-md mx-auto mb-8">
            <Card className="bg-primary/5 border-primary/20">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CreditCard className="h-6 w-6 text-primary" />
                    <span className="font-medium">
                      {isRTL ? "رصيدك الحالي" : "Your Credits"}
                    </span>
                  </div>
                  <Badge variant="secondary" className="text-lg px-4 py-1">
                    {remainingCredits}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Packages Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {packages.map((pkg, index) => (
            <Card 
              key={pkg.id} 
              className={`relative overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-xl ${getPackageStyle(pkg.credits)}`}
            >
              {pkg.credits >= 5 && pkg.credits < 10 && (
                <Badge className="absolute top-4 right-4 bg-purple-500">
                  {isRTL ? "الأكثر شيوعاً" : "Most Popular"}
                </Badge>
              )}
              {pkg.credits >= 10 && (
                <Badge className="absolute top-4 right-4 bg-yellow-500 text-black">
                  {isRTL ? "أفضل قيمة" : "Best Value"}
                </Badge>
              )}
              
              <CardHeader className="text-center pb-2">
                <div className="mx-auto mb-4">
                  {getPackageIcon(pkg.credits)}
                </div>
                <CardTitle className="text-2xl">
                  {isRTL ? pkg.name_ar : pkg.name_en}
                </CardTitle>
                <CardDescription className="text-base">
                  {isRTL ? pkg.description_ar : pkg.description_en}
                </CardDescription>
              </CardHeader>
              
              <CardContent className="text-center">
                <div className="mb-6">
                  <span className="text-5xl font-bold">{pkg.price_sar}</span>
                  <span className="text-xl text-muted-foreground mr-1">
                    {isRTL ? "ر.س" : "SAR"}
                  </span>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center gap-2 justify-center">
                    <Check className="h-5 w-5 text-green-500" />
                    <span>{pkg.credits} {isRTL ? "إضافات" : "submissions"}</span>
                  </div>
                  <div className="flex items-center gap-2 justify-center">
                    <Check className="h-5 w-5 text-green-500" />
                    <span>{isRTL ? "فعاليات" : "Events"}</span>
                  </div>
                  <div className="flex items-center gap-2 justify-center">
                    <Check className="h-5 w-5 text-green-500" />
                    <span>{isRTL ? "مساحات X" : "X Spaces"}</span>
                  </div>
                  <div className="flex items-center gap-2 justify-center">
                    <Check className="h-5 w-5 text-green-500" />
                    <span>{isRTL ? "حسابات تواصل" : "Social Accounts"}</span>
                  </div>
                </div>
              </CardContent>
              
              <CardFooter>
                <Button 
                  className="w-full" 
                  size="lg"
                  onClick={() => handlePurchase(pkg)}
                  disabled={purchasing === pkg.id}
                >
                  {purchasing === pkg.id ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : null}
                  {isRTL ? "اشتر الآن" : "Buy Now"}
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>

        {/* Features */}
        <div className="mt-16 text-center">
          <h2 className="text-2xl font-bold mb-8">
            {isRTL ? "ماذا يمكنك إضافته؟" : "What can you add?"}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Card className="p-6">
              <div className="text-4xl mb-4">🎉</div>
              <h3 className="font-bold mb-2">{isRTL ? "فعاليات" : "Events"}</h3>
              <p className="text-muted-foreground text-sm">
                {isRTL 
                  ? "أضف فعالياتك ومناسباتك للظهور في الموقع"
                  : "Add your events and occasions to appear on the site"}
              </p>
            </Card>
            <Card className="p-6">
              <div className="text-4xl mb-4">🎙️</div>
              <h3 className="font-bold mb-2">{isRTL ? "مساحات X" : "X Spaces"}</h3>
              <p className="text-muted-foreground text-sm">
                {isRTL 
                  ? "روّج لمساحاتك الصوتية على منصة X"
                  : "Promote your audio spaces on X platform"}
              </p>
            </Card>
            <Card className="p-6">
              <div className="text-4xl mb-4">👥</div>
              <h3 className="font-bold mb-2">{isRTL ? "حسابات تواصل" : "Social Accounts"}</h3>
              <p className="text-muted-foreground text-sm">
                {isRTL 
                  ? "أضف مجموعاتك على واتساب وتليجرام"
                  : "Add your WhatsApp and Telegram groups"}
              </p>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Packages;
